import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private lastToastKey = '';
  private lastToastAt = 0;

  constructor(private snackBar: MatSnackBar) {}

  private show(message: string, config: MatSnackBarConfig = {}): void {
    const now = Date.now();
    const panelClassKey = Array.isArray(config.panelClass)
      ? config.panelClass.join('|')
      : String(config.panelClass || '');
    const toastKey = `${panelClassKey}::${message}`;

    // Evita toasts duplicados disparados casi al mismo tiempo.
    if (toastKey === this.lastToastKey && now - this.lastToastAt < 1500) {
      return;
    }
    this.lastToastKey = toastKey;
    this.lastToastAt = now;

    // Dismiss any existing snackbar first
    this.snackBar.dismiss();

    const defaultConfig: MatSnackBarConfig = {
      duration: 2500,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: [],
      ...config
    };

    this.snackBar.open(message, undefined, defaultConfig);
  }

  success(message: string, duration = 2500): void {
    this.show(message, {
      duration,
      panelClass: ['toast-success']
    });
  }

  error(message: string, duration = 3000): void {
    this.show(message, {
      duration,
      panelClass: ['toast-error']
    });
  }

  warning(message: string, duration = 2500): void {
    this.show(message, {
      duration,
      panelClass: ['toast-warning']
    });
  }

  info(message: string, duration = 2500): void {
    this.show(message, {
      duration,
      panelClass: ['toast-info']
    });
  }

  // Métodos de conveniencia para casos específicos del dominio
  puestoSeleccionado(): void {
    this.success('Puesto seleccionado correctamente');
  }

  codigoGesActualizado(codigo: string): void {
    this.success(`Código GES actualizado a: ${codigo}`);
  }

  exposicionEliminada(): void {
    this.success('Exposición eliminada correctamente');
  }

  cambiosGuardados(): void {
    this.success('Los cambios se han guardado exitosamente');
  }

  errorGeneral(mensaje?: string): void {
    this.error(mensaje || 'Ha ocurrido un error inesperado. Intente nuevamente.');
  }

  archivoSubido(): void {
    this.success('Archivo subido correctamente');
  }

  archivoEliminado(): void {
    this.success('Documento eliminado correctamente');
  }

  validacionFallida(mensaje: string): void {
    this.warning(mensaje, 4500);
  }

  accionRequerida(mensaje: string): void {
    this.info(mensaje);
  }

  errorSubirArchivo(): void {
    this.error('Error al subir el archivo. Intente nuevamente.');
  }

  errorDescargarArchivo(): void {
    this.error('Error al descargar el archivo');
  }

  errorActualizarCodigo(): void {
    this.error('Error al actualizar el código GES');
  }

  errorEliminarExposicion(): void {
    this.error('Error al eliminar la exposición');
  }

  errorGuardarCambios(): void {
    this.error('Error al guardar cambios');
  }

  debeSeleccionarPuesto(): void {
    this.warning('Debe seleccionar un puesto primero');
  }

  debeDefinirExposicion(): void {
    this.warning('No puede adjuntar un archivo a este riesgo, primero debe definir un nivel de exposición.');
  }

  archivoMuyGrande(): void {
    this.warning('El archivo es demasiado grande. Máximo 30MB');
  }

  formatoArchivoInvalido(): void {
    this.warning('Formato no permitido. Solo se aceptan archivos PDF, Word y Excel.');
  }

  noHayDocumento(): void {
    this.info('No hay documento adjunto para este riesgo');
  }
}
